import React, { createContext, useContext, useEffect } from "react"
import type { ReactNode, CSSProperties } from "react"
import {
  usePopoverState,
  Popover as BasePopover,
  PopoverDisclosure,
  PopoverArrow
} from "reakit/Popover"
import type { PopoverStateReturn, PopoverInitialState } from "reakit/Popover"
import type { ExtractHTMLAttributes } from "reakit-utils/types"
import Portal from "../portal"
import cx from "classnames"
import { OldButton } from "../old-button"
import type { IconName } from "../icon"
import usePrevious from "../use-previous"

export function usePopover(
  ...args: Parameters<typeof usePopoverState>
): PopoverStateReturn {
  return usePopoverState(...args)
}

type PopoverContextValue = {
  popover: PopoverStateReturn
  label: string
  arrow: boolean
}

const PopoverContext = createContext<
  PopoverContextValue | Record<string, never>
>({})

export type PopoverProps = {
  /** Accessible label for the popover */
  label: string
  /** Whether to show the popover arrow */
  arrow?: boolean
  /** External popover state from usePopover() */
  popover?: PopoverStateReturn
  /** Popover content (typically PopoverButton + PopoverContent) */
  children?: ReactNode
}

/**
 * A compound popover component for displaying floating content triggered by a button.
 * Compose with PopoverButton and PopoverContent to build interactive popovers.
 */
function Popover({
  label,
  arrow = true,
  popover: providedPopover,
  children
}: PopoverProps) {
  const internalPopover = usePopoverState()

  const popover = providedPopover || internalPopover

  return (
    <PopoverContext.Provider value={{ popover, label, arrow }}>
      {children}
    </PopoverContext.Provider>
  )
}

export { Popover }
export default Popover

export type PopoverButtonProps = {
  /** Button content */
  children?: ReactNode
  /** Additional CSS classes */
  className?: string
  /** Accessible label */
  "aria-label"?: string
  /** Inline styles */
  style?: CSSProperties
  /** Button style variant */
  is?: "primary" | "primary-blue" | "secondary" | "tertiary" | "plain"
  /** Icon name */
  icon?: IconName
  /** Whether the button is disabled */
  disabled?: boolean
  /** Button size */
  size?: "sm" | "md" | "lg"
  /** Click handler */
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void
  /** Element id */
  id?: string
  /** Button title */
  title?: string
  /** aria-controls attribute */
  "aria-controls"?: string
}

export function PopoverButton({ children, ...props }: PopoverButtonProps) {
  const { popover } = useContext(PopoverContext) as PopoverContextValue

  return (
    <PopoverDisclosure {...popover}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {
        ((disclosureProps: ExtractHTMLAttributes<any>) =>
          React.cloneElement(
            <OldButton {...props}>{children}</OldButton>,
            disclosureProps
          )) as any
      }
    </PopoverDisclosure>
  )
}

export type PopoverContentProps = {
  /** Popover body content */
  children?: ReactNode
  /** Called when the popover closes */
  onClose?: () => void
  /** Styles applied to the overlay backdrop */
  overlayStyles?: CSSProperties
  /** Additional CSS classes */
  className?: string
  /** Inline styles */
  style?: CSSProperties
  /** Accessible label (overrides Popover label) */
  "aria-label"?: string
  /** Popover placement relative to the trigger */
  placement?: PopoverInitialState["placement"]
  /** Whether to show the arrow (overrides Popover arrow) */
  arrow?: boolean
  /** Tab index for the popover content */
  tabIndex?: number
}

export function PopoverContent({
  children,
  onClose,
  overlayStyles = {},
  ...props
}: PopoverContentProps) {
  const { label, arrow, popover } = useContext(
    PopoverContext
  ) as PopoverContextValue

  const className = cx(
    "border border-gray-200 rounded shadow bg-white outline-none",
    props.className
  )

  const previouslyOpen = usePrevious(popover.visible)

  useEffect(() => {
    if (previouslyOpen && !popover.visible && onClose) {
      onClose()
    }
  }, [previouslyOpen, popover.visible, onClose])

  return (
    <Portal>
      {popover.visible && (
        <PopoverOverlay onClick={popover.hide} overlayStyles={overlayStyles} />
      )}
      <div style={{ position: "relative" }}>
        <BasePopover
          {...props}
          {...popover}
          className={className}
          aria-label={label}
          hideOnClickOutside={false}
        >
          {arrow && <PopoverArrow className="popover-arrow" {...popover} />}
          {children}
        </BasePopover>
      </div>
    </Portal>
  )
}

// PopoverOverlay is rendered so that clicking on it (outside the popover) will
// close the popover. reakit/Popover uses document event listeners, which can
// cause issues when clicking on our dropdown menus, because they technically
// render outside of the popover through portals.
type PopoverOverlayProps = {
  overlayStyles?: CSSProperties
  onClick?: () => void
}

function PopoverOverlay({ overlayStyles, ...props }: PopoverOverlayProps) {
  return (
    <div
      {...props}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        ...overlayStyles
      }}
    ></div>
  )
}
